import type { Column } from "@tanstack/react-table";
import { Check, PlusCircle, XCircle } from "lucide-react";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type DataTableFacetedFilterOption = {
  label: string;
  value: string;
  count?: number;
  badgeClassName?: string;
};

type DataTableFacetedFilterProps<TData, TValue> = {
  column?: Column<TData, TValue>;
  title: string;
  options: DataTableFacetedFilterOption[];
  multiple?: boolean;
};

function toSelectedValues(value: unknown): Set<string> {
  if (Array.isArray(value)) {
    return new Set(value.map((entry) => String(entry)));
  }
  if (typeof value === "string" && value.trim()) {
    return new Set([value]);
  }
  return new Set();
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  multiple = true,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const [open, setOpen] = React.useState(false);
  const selectedValues = toSelectedValues(column?.getFilterValue());

  const onOptionToggle = React.useCallback(
    (optionValue: string) => {
      if (!column) return;
      const currentSelectedValues = toSelectedValues(column.getFilterValue());
      const isSelected = currentSelectedValues.has(optionValue);
      if (!multiple) {
        column.setFilterValue(isSelected ? undefined : optionValue);
        setOpen(false);
        return;
      }

      const next = new Set(currentSelectedValues);
      if (isSelected) {
        next.delete(optionValue);
      } else {
        next.add(optionValue);
      }
      const values = [...next];
      column.setFilterValue(values.length > 0 ? values : undefined);
    },
    [column, multiple],
  );

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      column?.setFilterValue(undefined);
    },
    [column],
  );

  const selectedOptions = React.useMemo(
    () => options.filter((option) => selectedValues.has(option.value)),
    [options, selectedValues],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 border-dashed font-normal"
        >
          {selectedValues.size > 0 ? (
            <div
              role="button"
              tabIndex={0}
              aria-label={`Clear ${title} filter`}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              onClick={onReset}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onReset();
                }
              }}
            >
              <XCircle className="size-4" />
            </div>
          ) : (
            <PlusCircle className="size-4" />
          )}
          <span>{title}</span>
          {selectedValues.size > 0 ? (
            <>
              <span className="mx-0.5 h-4 w-px bg-border" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden items-center gap-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  selectedOptions.map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className={cn("rounded-sm px-1 font-normal", option.badgeClassName)}
                    >
                      {option.label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <Command>
          <CommandInput placeholder={`Search ${title.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup className="max-h-[260px] overflow-y-auto">
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.value}`}
                    onSelect={() => onOptionToggle(option.value)}
                  >
                    <div
                      className={cn(
                        "flex size-4 items-center justify-center rounded-sm border border-primary",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="size-3" />
                    </div>
                    <span className="truncate">{option.label}</span>
                    {typeof option.count === "number" ? (
                      <span className="ml-auto font-mono text-xs text-muted-foreground">
                        {option.count}
                      </span>
                    ) : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 ? (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onReset()}
                    className="justify-center text-center"
                  >
                    Clear filter
                  </CommandItem>
                </CommandGroup>
              </>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
